import { CDButton } from "@cdatacloud/component-library";
import { useMutation } from "@tanstack/react-query";
import { CDataModalV2 } from "src/components/modal/CDataModalV2";
import { UserRole } from "src/models";
import { useAppSelector } from "src/redux/hooks";
import { logoutUser } from "src/routes/AuthorizeContext";
import { tosLastUpdated } from "src/utility/TermsOfServiceLastUpdated";
import { updateAccountTOSAcceptance } from "./updateAccountTOSAcceptance";

interface ITermsOfServiceModal {
  displayed: boolean;
  setDisplayed: (displayed: boolean) => void;
}

export const TermsOfServiceModal = (props: ITermsOfServiceModal) => {
  const { displayed, setDisplayed } = props;
  const accountInfo = useAppSelector((s) => s.accountInfo);
  const userInfo = useAppSelector((s) => s.user);
  const isQueryUser = userInfo.role === UserRole.Query;

  const { mutateAsync: updateTOSAcceptanceAsync } = useMutation({
    mutationKey: [`/account/setTosAcceptedDate`],
    mutationFn: updateAccountTOSAcceptance,
    meta: {
      errorMessage: "Failed to update the Terms of Service acceptance date",
    },
    onSuccess: () => setDisplayed(false),
  });

  function mailToAdmin() {
    window.location.href = `mailto:${accountInfo.contactEmail}`;
  }

  let title: string;
  let body, primaryButton: React.ReactNode;

  if (isQueryUser) {
    title = "We've Updated Our Terms of Service";
    body = (
      <>
        To continue using Connect Cloud, please contact your account
        administrator to accept the latest updates to our Terms of Service.
      </>
    );
    primaryButton = (
      <CDButton onClick={mailToAdmin} data-testid="button-accept-tos">
        Contact Administrator
      </CDButton>
    );
  } else {
    title = "Accept New Terms and Conditions";
    body = (
      <div>
        Our{" "}
        <a
          href="https://cloud.cdata.com/docs/terms-of-service.html"
          className="text-primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service
        </a>{" "}
        have changed. Please accept the new terms to continue.
      </div>
    );
    primaryButton = (
      <CDButton
        onClick={() => updateTOSAcceptanceAsync(tosLastUpdated)}
        data-testid="button-accept-tos"
      >
        I agree
      </CDButton>
    );
  }

  if (!displayed) return null;

  return (
    <CDataModalV2
      close={() => setDisplayed(false)}
      displayToggleCloseButton={false}
      spacedFooter={false}
      displayed={displayed}
      title={title}
      primaryButton={primaryButton}
      secondaryButton={
        <CDButton
          color="Secondary/Text Regular"
          onClick={() => logoutUser()}
          className="logout-button"
          data-testid="button-logout"
        >
          Return to Login
        </CDButton>
      }
    >
      {body}
    </CDataModalV2>
  );
};
